import { useEffect, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ListChecks,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import { isChecklistItemDone, taskDueBucket, type ClientTask } from '@peles/shared';
import {
  useClientTasks,
  useCreateClientTask,
  useDeleteClientTask,
  useToggleClientTask,
  useUpdateClientTask,
} from '../../hooks/usePortal';
import type { PortalTaskScope } from '../../api/portal';
import { useLocalDay } from '../../hooks/useDashboard';
import { useDialog } from '../../context/DialogContext';
import { DropdownMenu } from '../common/DropdownMenu';
import { EntityModal } from '../common/EntityModal';
import { QuickAddTask } from '../tasks/QuickAddTask';
import { DueDatePicker } from '../tasks/DueDatePicker';
import { TaskCheckbox, TaskDueBadge } from '../tasks/TaskRow';

export interface ClientTaskListProps {
  eventId: string;
  /** For "Week before event" / "Event day" when picking a date. */
  eventStartAt?: Date | string | null;
  /**
   * The event is cancelled: it has no tasks left, and none can be added —
   * the server refuses them — so nothing is offered.
   */
  cancelled?: boolean;
  /**
   * May add tasks of their own — a client's `canManageTasks`, a vendor's
   * "Add tasks" grant. The tasks assigned to them are shown and ticked either way.
   */
  canAdd?: boolean;
  /** Whose portal: the client's (default) or the vendor's. */
  scope?: PortalTaskScope;
}

const fieldClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function EditTaskModal({
  task,
  todayKey,
  eventStartAt,
  saving,
  onClose,
  onSave,
}: {
  task: ClientTask;
  todayKey: string;
  eventStartAt?: Date | string | null;
  saving: boolean;
  onClose: () => void;
  onSave: (input: { title: string; dueDate: string | null; notes: string | null }) => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [dueDate, setDueDate] = useState(
    task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : '',
  );
  const [notes, setNotes] = useState(task.notes ?? '');
  const save = () =>
    title.trim() &&
    onSave({ title: title.trim(), dueDate: dueDate || null, notes: notes.trim() || null });

  return (
    <EntityModal
      open
      onClose={onClose}
      title="Edit task"
      size="sm"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!title.trim() || saving}
            className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Task</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            maxLength={500}
            className={fieldClass}
            autoFocus
          />
        </label>
        <div>
          <span className="block text-xs font-medium text-gray-600 mb-1">Due</span>
          <DueDatePicker
            value={dueDate}
            onChange={setDueDate}
            todayKey={todayKey}
            eventStartAt={eventStartAt}
            placeholder="No due date"
          />
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            className={fieldClass}
          />
        </label>
      </div>
    </EntityModal>
  );
}

function ClientTaskRow({
  task,
  todayKey,
  onToggle,
  onEdit,
  onDelete,
}: {
  task: ClientTask;
  todayKey: string;
  onToggle: (task: ClientTask) => void;
  onEdit: (task: ClientTask) => void;
  onDelete: (task: ClientTask) => void;
}) {
  const done = isChecklistItemDone(task);
  return (
    <div className="flex items-start gap-3 px-4 py-3" data-testid={`client-task-${task.id}`}>
      <TaskCheckbox
        checked={done}
        onChange={() => onToggle(task)}
        label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
      />
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-6 ${done ? 'line-through text-gray-400' : 'text-gray-900'}`}>
          {task.title}
        </p>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-0.5 empty:hidden">
          {task.dueDate && !done && <TaskDueBadge dueDate={task.dueDate} todayKey={todayKey} />}
          {!task.isOwn && <span className="text-xs text-gray-500">From the office</span>}
        </div>
        {task.notes && (
          <p className="text-xs text-gray-500 mt-1 whitespace-pre-line">{task.notes}</p>
        )}
      </div>
      {task.isOwn && (
        <DropdownMenu
          ariaLabel={`Task ${task.title}`}
          align="end"
          trigger={(props) => (
            <button
              type="button"
              {...props}
              aria-label={`Actions for ${task.title}`}
              className="shrink-0 p-1.5 -mr-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            >
              <MoreHorizontal size={16} />
            </button>
          )}
          items={[
            { id: 'edit', label: 'Edit', icon: <Pencil size={14} />, onSelect: () => onEdit(task) },
            {
              id: 'delete',
              label: 'Delete',
              icon: <Trash2 size={14} />,
              dividerBefore: true,
              onSelect: () => onDelete(task),
            },
          ]}
        />
      )}
    </div>
  );
}

/**
 * A client's or vendor's own tasks for an event: the ones the office assigned
 * them and the ones they added. They tick either kind off; they edit and
 * delete only what they wrote, and add their own only when the office lets
 * them (`canAdd`).
 */
export function ClientTaskList({
  eventId,
  eventStartAt,
  cancelled = false,
  canAdd = true,
  scope = 'client',
}: ClientTaskListProps) {
  const { date: todayKey } = useLocalDay();
  const { confirm } = useDialog();
  const {
    data: tasks = [],
    isLoading,
    isError,
    refetch,
  } = useClientTasks(eventId, !cancelled, scope);
  const create = useCreateClientTask(eventId, scope);
  const update = useUpdateClientTask(eventId, scope);
  const remove = useDeleteClientTask(eventId, scope);
  const toggle = useToggleClientTask(eventId, scope);
  const [editing, setEditing] = useState<ClientTask | null>(null);
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    if (editing && !tasks.some((t) => t.id === editing.id)) setEditing(null);
  }, [tasks, editing]);

  const open = tasks.filter((t) => !isChecklistItemDone(t));
  const done = tasks.filter(isChecklistItemDone);
  const overdue = open.filter((t) => taskDueBucket(t.dueDate, todayKey) === 'overdue').length;

  async function onDelete(task: ClientTask) {
    if (
      await confirm({
        title: 'Delete task',
        message: `Delete “${task.title}”?`,
        confirmLabel: 'Delete',
        variant: 'danger',
      })
    ) {
      remove.mutate(task.id);
    }
  }

  const onToggle = (task: ClientTask) =>
    toggle.mutate({ taskId: task.id, isDone: !isChecklistItemDone(task) });

  if (cancelled) {
    return (
      <section
        className="bg-white rounded-xl border border-gray-200 px-4 py-10 text-center"
        aria-label="Your tasks"
      >
        <ListChecks className="mx-auto h-9 w-9 text-gray-300" />
        <p className="mt-2 text-sm text-gray-500">
          This event is cancelled, so there are no tasks for it.
        </p>
      </section>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2" aria-label="Loading your tasks">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 rounded-lg bg-white border border-gray-200 animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-10 bg-white rounded-xl border border-gray-200">
        <p className="text-sm text-gray-600">Your tasks could not be loaded.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <section className="bg-white rounded-xl border border-gray-200" aria-label="Your tasks">
      <header className="px-4 pt-4 pb-2">
        <h2 className="text-lg font-semibold text-gray-900">Your tasks</h2>
        <p className="text-sm text-gray-500" data-testid="client-task-summary">
          {open.length === 0
            ? 'Nothing to do right now.'
            : `${open.length} to do${overdue ? ` · ${overdue} overdue` : ''}`}
        </p>
      </header>

      {canAdd && (
        <QuickAddTask
          todayKey={todayKey}
          eventStartAt={eventStartAt}
          placeholder="Add a task for yourself…"
          onAdd={({ title, dueDate }) => create.mutate({ title, ...(dueDate ? { dueDate } : {}) })}
        />
      )}

      {open.length === 0 && done.length === 0 ? (
        <div className="px-4 py-10 text-center border-t border-gray-100">
          <ListChecks className="mx-auto h-9 w-9 text-gray-300" />
          <p className="mt-2 text-sm text-gray-500">
            {canAdd
              ? 'No tasks yet. Add one above, or the office will send you some.'
              : 'No tasks for you yet. The office will send you any.'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 border-t border-gray-100">
          {open.map((task) => (
            <ClientTaskRow
              key={task.id}
              task={task}
              todayKey={todayKey}
              onToggle={onToggle}
              onEdit={setEditing}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {done.length > 0 && (
        <div className="border-t border-gray-100">
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            aria-expanded={showDone}
            className="w-full flex items-center gap-1 px-4 py-2.5 text-sm text-gray-500 hover:text-gray-800"
          >
            {showDone ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            {done.length} done
          </button>
          {showDone && (
            <div className="divide-y divide-gray-100">
              {done.map((task) => (
                <ClientTaskRow
                  key={task.id}
                  task={task}
                  todayKey={todayKey}
                  onToggle={onToggle}
                  onEdit={setEditing}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <EditTaskModal
          task={editing}
          todayKey={todayKey}
          eventStartAt={eventStartAt}
          saving={update.isPending}
          onClose={() => setEditing(null)}
          onSave={(input) =>
            update.mutate({ taskId: editing.id, input }, { onSuccess: () => setEditing(null) })
          }
        />
      )}
    </section>
  );
}
